/* eslint-disable */
import { Component, ElementRef, NgZone, OnInit, ViewChild } from '@angular/core';
import { AlertController, ModalController, NavParams } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { CONSTANTS } from 'src/app/services/config.service';
import { LoggerService } from 'src/app/services/logger.service';
import { MapsService } from 'src/app/services/maps.service';
import { PubSubService } from 'src/app/services/pub-sub.service';
import { MSRemoteLoggerCategory } from 'src/app/adapters/remote-logger/utils/remote-logger.utils';

@Component({
  selector: 'app-browser-geocoder',
  templateUrl: './browser-geocoder.page.html',
  styleUrls: ['./browser-geocoder.page.scss']
})
export class BrowserGeocoderPage implements OnInit {
  @ViewChild('map') mapElement: ElementRef;
  @ViewChild('placesMap') placesMap: ElementRef;

  data: any = {};
  survey: any = {};
  point: any;
  center: any;
  stop: any = {};

  searchType = 'search';

  query = '';
  // searchPlaceholder: string;
  searchResults: any = [];
  hasValidAccuracy: boolean;
  inaccurateMsg = {
    title: this.translate.instant('MAP_PLACE_SEARCH_INACCURATE'),
    snippet: this.translate.instant('MAP_PLACE_SEARCH_MORE_SPECIFIC')
  };
  invalidMapSearch = false;
  zoomedInEnough = false;

  nextDisabled: boolean;
  mapLoaded = false;
  placesLoaded = false;

  places: any = [];
  location: any;

  constructor(
    private alertCtrl: AlertController,
    private navParams: NavParams,
    private zone: NgZone,
    private modalCtrl: ModalController,
    private translate: TranslateService,
    private logger: LoggerService,
    private maps: MapsService,
    private pubSub: PubSubService
  ) {
    // this.searchPlaceholder = this.translate.instant('MAP_PLACE_SEARCH_LOADING');
    this.nextDisabled = true;

    this.pubSub.subscribe(CONSTANTS.EVENTS.mapViewChanged, (...args) => {
      this.logger.log('Map Changed', MSRemoteLoggerCategory.BROWSER_GEOCODER_PAGE);
      this.onViewChangeEnd();
    });
  }

  // closeKeyboard() {
  //     if (CONSTANTS.IS_DEVICE) {
  //         this.keyboard.hide();
  //     }
  // }

  /************************************************************************************************************************************************************************
   * HELP
   ***********************************************************************************************************************************************************************/

  async onClickHelp() {
    this.maps.presentAlertGeocoderInstructions();
  }

  /************************************************************************************************************************************************************************
   * MANIPULATE MAP
   ***********************************************************************************************************************************************************************/

  addInfoWindow() {
    const data = {
      markerIdx: 0,
      content: this.getStartMarkerText()
    };

    this.maps.addInfoWindow(data);
  }

  getStartMarkerText() {
    if (this.hasValidAccuracy === false) {
      return this.inaccurateMsg;
    }

    const title: string = '<span class="info-window-title">' + this.stop.label + '</span>';
    const snippet: string = this.location ? this.location.name : this.stop.address;

    return { title, snippet };
  }

  updateMapAndMarker() {
    if (this.maps.infoWindows.length > 0) {
      this.maps.closeInfoWindow(this.maps.infoWindows[0]);
    }

    this.maps.setMapCenter({ lat: this.location.lat, lng: this.location.lng }).then(() => {
      this.maps.setMarkerPosition(this.maps.markers[0], { lat: this.location.lat, lng: this.location.lng });
      this.maps.setMarkerVisible(this.maps.markers[0], true);
      this.maps.setMapZoom(16);
      this.addInfoWindow();
      if (this.hasValidAccuracy) {
        this.nextDisabled = false;
      }
    });
  }

  /************************************************************************************************************************************************************************
   * ADDRESS SEARCH
   ***********************************************************************************************************************************************************************/

  onAddressSearchEvent($event) {
    this.logger.log($event, MSRemoteLoggerCategory.BROWSER_GEOCODER_PAGE);

    if ($event.type === 'clear') {
      this.onClearSearch();
    } else {
      this.onSelectAddress($event);
    }
  }

  onSelectAddress(res) {
    this.location = res.location;
    this.hasValidAccuracy = res.hasValidAccuracy;
    this.updateMapAndMarker();
  }

  onClearSearch() {
    this.location = null;
    if (this.maps.infoWindows.length) {
      this.maps.closeInfoWindow(this.maps.infoWindows[0]);
    }
    this.maps.setMarkerVisible(this.maps.markers[0], false);
  }

  onClickSearchSegment() {
    this.searchType = 'search';
    this.nextDisabled = true;

    if (this.maps.infoWindows.length) {
      // close any infowindows
      this.maps.closeInfoWindow(this.maps.infoWindows[0]); //this.maps.infoWindows[0].close();
    }
  }

  /************************************************************************************************************************************************************************
   * MAP SEARCH
   ***********************************************************************************************************************************************************************/

  onViewChangeEnd() {
    if (this.searchType !== 'map') {
      return;
    }

    // page wasn't automatically updating. needed to add this
    this.zone.run(() => {
      this.invalidMapSearch = false;
      this.zoomedInEnough = this.maps.getZoom() >= 16;
      this.nextDisabled = true;
      if (this.zoomedInEnough) {
        this.location = this.maps.getMapCenter();
        this.maps
          .reverseGeocode(this.location)
          .then((res) => {
            this.location = res;
            this.nextDisabled = false;
          })
          .catch((res) => {
            // this.location.name = ""
            // console.log(res);
            this.location.name = null;
            this.invalidMapSearch = true;
          });
      }
    });
  }

  onClickMapSegment() {
    this.searchType = 'map';
    this.query = '';
    this.searchResults = [];
    this.location = null;

    this.onViewChangeEnd();

    if (this.maps.infoWindows.length) {
      // close any infowindows
      this.maps.closeInfoWindow(this.maps.infoWindows[0]); //this.maps.infoWindows[0].close();
    }
    this.maps.setMarkerVisible(this.maps.markers[0], false);
  }

  /************************************************************************************************************************************************************************
   * MAP MODE
   ***********************************************************************************************************************************************************************/

  toggleSearchType() {
    if (this.searchType === 'search') {
      this.onClickMapSegment();
    } else {
      this.onClickSearchSegment();
    }
  }

  /************************************************************************************************************************************************************************
   * PREPARE DATA FOR STORING
   ***********************************************************************************************************************************************************************/

  getLatLon() {
    // can only get here if next enabled, meaning you have survey data for this location
    if (!this.location && this.searchType !== 'map') {
      return Promise.resolve(null);
    } // no need to update if started with a location and never changed

    return new Promise<void>((resolve) => {
      if (this.searchType === 'map') {
        this.location = this.maps.getMapCenter();
        this.maps
          .reverseGeocode(this.location)
          .then((res) => {
            this.stop.address = this.translate.instant('MAP_MAP_SEARCH_PLACE_NEAR') + ' ' + res['name'];
            this.stop.lat = res['location'].latitude;
            this.stop.lng = res['location'].longitude;

            resolve();
          })
          .catch((err) => {
            // console.log(err);
            this.maps.setMarkerPosition(this.maps.markers[0], this.maps.getMapCenter());
            this.maps.addInfoWindow({ markerIdx: 0, content: { title: this.translate.instant('MAP_PLACE_SEARCH_BAD_GEOCODE') } });
          });
      } else {
        this.stop.address = this.location.name;
        this.stop.lat = this.location.lat;
        this.stop.lng = this.location.lng;
        resolve();
      }
    });
  }

  next() {
    this.getLatLon().then(() => {
      this.modalCtrl.dismiss(this.stop);
    });
  }

  dismiss() {
    this.modalCtrl.dismiss();
  }

  init() {
    // console.log('ionViewDidLoad GeocoderBrowserPage');
    let hasLoc = false;
    const stopIdx = this.navParams.get('stopIdx');
    const stops = this.navParams.get('stops');
    const homeLoc = this.navParams.get('homeLoc');
    const thisStop = stops[stopIdx];
    const prevStop = stopIdx > 0 ? stops[stopIdx - 1] : null;
    let center = null;
    let mapCenter = null;
    let position = { lat: 0, lng: 0 };

    // const inputs: any = document.getElementById("searchbar-input").getElementsByTagName("INPUT");
    // inputs[0].disabled = true;

    this.stop = thisStop;

    if (thisStop.lat != null && thisStop.lng !== null) {
      hasLoc = true;
      center = thisStop;
    } else if (prevStop && prevStop.lat != null && prevStop.lng !== null) {
      center = prevStop;
    } else if (homeLoc && homeLoc.lat != null && homeLoc.lng !== null) {
      center = homeLoc;
    }

    // mapCenter is either lat/lng or null
    // position is either dummy lat/lng or real lat/lng

    if (center) {
      mapCenter = { lat: center.lat, lng: center.lng };
      position = { lat: center.lat, lng: center.lng };
    }

    this.maps
      .init(this.mapElement.nativeElement, mapCenter, null)
      .then((res) => {
        const startMarkerOpts = {
          position,
          icon: { url: './assets/imgs/marker-generic.svg' },
          visible: hasLoc
        };

        if (res) {
          // this.searchPlaceholder = this.translate.instant('MAP_PLACE_SEARCH_SEARCH');
          // inputs[0].disabled = false;
        }

        this.maps.addViewChangeEndEvent();
        return this.maps.addMarker(startMarkerOpts);
      })
      .then(() => {
        this.mapLoaded = true;
        if (hasLoc) {
          this.addInfoWindow();
          this.nextDisabled = false;
        }
      });
  }

  ionViewWillEnter() {
    this.init();
  }

  ngOnInit() {}
}
